import { useState, useCallback, useMemo, useRef } from "react"
import { getAvatarColor } from "@vc/utils/avatarColors"

export interface PictureInPictureState {
  isActive: boolean
  isSupported: boolean
}

export type PictureInPictureControls = {
  onToggleMic?: () => void
  onToggleCam?: () => void
  onLeave?: () => void
  participantName?: string
}

/**
 * One tile in the PiP window. Keyed by `id` so the window can be reconciled in
 * place as people join, leave, or take over as active speaker — rebuilding the
 * grid wholesale would re-attach every srcObject and flash the surviving tiles.
 */
export interface PipParticipant {
  id: string
  name: string
  stream: MediaStream | null
  isLocal?: boolean
  /**
   * The bare name the camera-off avatar is drawn from, when `name` carries a
   * label ("Varun Eliday (You)"). Defaults to `name`.
   */
  avatarName?: string
}

/** Up to two initials, as the main video tile shows them: "Varun Eliday" → "VE". */
export function pipInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "U"
}

/** Local mic/cam state, mirrored onto the PiP buttons so they read correctly
 *  no matter which surface toggled them. */
export interface PipMediaState {
  micOn: boolean
  camOn: boolean
}

type PipVideoInput = HTMLVideoElement | HTMLVideoElement[] | null | undefined

export function usePictureInPicture(): {
  pipState: PictureInPictureState
  togglePictureInPicture: (
    participants?: PipParticipant[] | PipVideoInput,
    controls?: PictureInPictureControls,
    mediaState?: PipMediaState,
  ) => Promise<void>
  /** Reconcile an already-open PiP window. No-op when closed. */
  syncPictureInPicture: (participants: PipParticipant[], mediaState?: PipMediaState) => void
  closePictureInPicture: () => Promise<void>
} {
  const [isActive, setIsActive] = useState(false)
  // Compute support synchronously during render
  const isSupported = useMemo(() => "documentPictureInPicture" in window, [])
  const pipWindowRef = useRef<Window | null>(null)

  // Handles into the open PiP document. Held in a ref (not state) because the
  // window is plain DOM in another document — React never renders into it, so
  // syncing is imperative and must not trigger a re-render of the host tree.
  const renderRef = useRef<{
    win: Window
    grid: HTMLDivElement
    tiles: Map<string, { tile: HTMLDivElement; video: HTMLVideoElement | null; name: HTMLDivElement }>
    renderTiles: (participants: PipParticipant[]) => void
    applyMediaState: (state: PipMediaState) => void
  } | null>(null)

  /**
   * Close whichever Picture-in-Picture this browser gave us.
   *
   * BOTH kinds, because `togglePictureInPicture` opens either one: Document PiP
   * where it exists, and element-level PiP on Safari and Firefox where it does
   * not. This closed only the Document window, so on those browsers the
   * floating video outlived the thing it was showing — the same visible bug,
   * reachable only on the browsers nobody reports from.
   *
   * Safe to call when nothing is open: each branch is guarded, and neither
   * throwing is allowed to stop the other from running.
   */
  const closePictureInPicture = useCallback(async () => {
    try {
      const pipWindow = (window as unknown as { documentPictureInPicture?: { window: Window | null } }).documentPictureInPicture?.window
      if (pipWindow) {
        pipWindow.close()
        pipWindowRef.current = null
      }
    } catch {
      // Already gone, or the window was closed by the user.
    }

    try {
      // The Safari / Firefox fallback. `pictureInPictureElement` is null unless
      // element PiP is actually showing, so this is a no-op under Document PiP.
      if (document.pictureInPictureElement) await document.exitPictureInPicture()
    } catch {
      // Exiting can reject if the element went away first. Nothing to recover.
    }

    setIsActive(false)
  }, [])

  const togglePictureInPicture = useCallback(async (
    participants?: PipParticipant[] | PipVideoInput,
    controls?: PictureInPictureControls,
    mediaState?: PipMediaState,
  ) => {
    try {
      // Accepts either the participant model or bare <video> elements. The
      // element form is the legacy shape kept for the no-Document-PiP fallback
      // below, which can only ever pop out a single element.
      const isParticipantList = Array.isArray(participants)
        && participants.every((p) => p != null && typeof p === "object" && "id" in p)
      const participantList: PipParticipant[] = isParticipantList
        ? (participants as PipParticipant[])
        : []
      const videoElement: PipVideoInput = isParticipantList
        ? undefined
        : (participants as PipVideoInput)
      const docPip = (window as unknown as {
        documentPictureInPicture?: {
          window: Window | null
          requestWindow: (options?: { width?: number; height?: number }) => Promise<Window>
        }
      }).documentPictureInPicture

      if (!docPip) {
        // Safari / Firefox: no Document PiP. Degrade to element-level PiP, which
        // can only carry one video — pick the first tile the caller ranked
        // (local, or the active speaker when the caller puts them first).
        const fallbackEl: HTMLVideoElement | null =
          (Array.isArray(videoElement) ? videoElement[0] : videoElement)
          ?? document.querySelector<HTMLVideoElement>('video[data-pip-video="true"]')

        if (fallbackEl && typeof fallbackEl.requestPictureInPicture === "function") {
          if (document.pictureInPictureElement === fallbackEl) {
            await document.exitPictureInPicture()
          } else {
            await fallbackEl.requestPictureInPicture()
          }
        } else {
          console.warn("Picture-in-Picture is not supported in this browser")
        }
        return
      }

      // If PiP window is already open, close it
      if (docPip.window) {
        docPip.window.close()
        setIsActive(false)
        pipWindowRef.current = null
        return
      }

      // Open new PiP window
      const pipWindow = await docPip.requestWindow({
        width: 360,
        height: 640,
      })

      pipWindowRef.current = pipWindow

      // Mutable holder rather than the captured `controls` object. The PiP
      // window outlives many host re-renders; binding listeners straight to the
      // closure from open-time would pin the first set of handlers forever, so
      // "Leave meeting" could act on a stale meeting reference. syncPictureInPicture
      // refreshes this in place and the listeners read through it.
      const controlsRef: PictureInPictureControls = { ...(controls ?? {}) }

      // Copy styles to PiP window
      const styleSheet = document.createElement("style")
      styleSheet.textContent = `
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          background: #202124;
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        .pip-container {
          position: relative;
          width: 100%;
          height: 100vh;
          background: #202124;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          justify-content: flex-start;
          color: white;
        }
        .pip-header {
          height: 48px;
          padding: 0 12px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: rgba(255, 255, 255, 0.8);
          font-size: 13px;
        }
        .pip-header .dot {
          width: 6px;
          height: 6px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.6);
        }
        .pip-stage {
          flex: 1;
          display: flex;
          padding: 8px;
        }
        .pip-grid {
          display: flex;
          flex: 1;
          gap: 8px;
          align-items: stretch;
        }
        .pip-grid.two-tiles {
          flex-direction: row;
        }
        .pip-grid.vertical {
          flex-direction: column;
        }
        /* Alone in the call: one tile, full stage. Previously the grid was
           forced to a 2-tile minimum, which showed a duplicate of your own
           feed (or an empty placeholder) next to you. */
        .pip-grid.single {
          flex-direction: row;
        }
        .pip-tile {
          position: relative;
          flex: 1;
          min-width: 0;
          border-radius: 16px;
          overflow: hidden;
          background: #111;
        }
        .pip-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .pip-name {
          position: absolute;
          left: 10px;
          bottom: 10px;
          padding: 6px 10px;
          border-radius: 999px;
          background: rgba(0,0,0,0.55);
          backdrop-filter: blur(6px);
          font-size: 12px;
          color: rgba(255,255,255,0.9);
        }
        .pip-controls {
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 0 12px 14px;
        }
        .pip-btn {
          width: 44px;
          height: 44px;
          border-radius: 999px;
          border: none;
          cursor: pointer;
          display: grid;
          place-items: center;
          background: #3c4043;
          color: white;
        }
        .pip-btn:hover { background: #5f6368; }
        .pip-btn.danger { background: #ea4335; }
        .pip-btn.danger:hover { background: #d33426; }
        /* Red = that track is OFF, matching the main control bar. Driven by
           applyMediaState so the button is correct whichever surface toggled. */
        .pip-btn.off { background: #ea4335; }
        .pip-btn.off:hover { background: #d33426; }
        .pip-btn .slash { display: none; }
        .pip-btn.off .slash { display: block; }
        .pip-icon { width: 20px; height: 20px; }
      `
      pipWindow.document.head.appendChild(styleSheet)

      // Create content container
      const container = pipWindow.document.createElement("div")
      container.className = "pip-container"

      const header = pipWindow.document.createElement("div")
      header.className = "pip-header"
      const headerDot = pipWindow.document.createElement("span")
      headerDot.className = "dot"
      const headerText = pipWindow.document.createElement("span")
      headerText.textContent = window.location.host
      header.appendChild(headerDot)
      header.appendChild(headerText)
      container.appendChild(header)

      const stage = pipWindow.document.createElement("div")
      stage.className = "pip-stage"

      const grid = pipWindow.document.createElement("div")
      grid.className = "pip-grid"

      type TileEntry = {
        id: string
        tile: HTMLDivElement
        video: HTMLVideoElement | null
        name: HTMLDivElement
        // null on remote tiles — the ⋮ menu is local-only (see createTile).
      }

      // Insertion-ordered: local first, then whoever the caller ranked next
      // (active speaker). Map keeps identity stable across re-renders.
      const tileMap = new Map<string, TileEntry>()
      const orderedTiles = (): TileEntry[] => Array.from(tileMap.values())
      // Kept as a live view so the layout code below reads current tiles.
      const pipTiles = orderedTiles

      const createTile = (participant: PipParticipant) => {
        const srcVideo = participant.stream
        const tile = pipWindow.document.createElement("div") as HTMLDivElement
        tile.className = "pip-tile"
        tile.classList.toggle("is-local", !!participant.isLocal)

        let pipVideoEl: HTMLVideoElement | null = null
        if (srcVideo) {
          const pipVideo = pipWindow.document.createElement("video")
          pipVideo.className = "pip-video"
          pipVideo.srcObject = srcVideo
          pipVideo.autoplay = true
          // Muted is mandatory, not cosmetic: the tiles carry the same remote
          // streams the main window is already playing. Unmuted here you would
          // hear every remote participant twice, slightly out of phase.
          pipVideo.muted = true
          pipVideo.playsInline = true
          tile.appendChild(pipVideo)
          pipVideoEl = pipVideo
          pipVideo.play().catch(() => { /* autoplay race — harmless */ })
        } else {
          // Camera off: who it is, not a camera icon. The same face the main
          // tile shows in this state: the name's pastel colour behind the
          // initials in a light circle. Built with textContent, not innerHTML:
          // a name is whatever the person typed.
          const avatarName = participant.avatarName ?? participant.name
          const placeholder = pipWindow.document.createElement("div")
          placeholder.className = "pip-avatar"
          placeholder.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            background: ${getAvatarColor(avatarName)};
          `
          const circle = pipWindow.document.createElement("div")
          circle.className = "pip-initials"
          circle.style.cssText = `
            width: min(40%, 120px);
            aspect-ratio: 1;
            border-radius: 999px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255,255,255,0.3);
            color: #1e293b;
            font-size: clamp(16px, 9vmin, 40px);
            font-weight: 500;
          `
          circle.textContent = pipInitials(avatarName)
          placeholder.appendChild(circle)
          tile.appendChild(placeholder)
        }

        const name = pipWindow.document.createElement("div")
        name.className = "pip-name"
        name.textContent = participant.name
        tile.appendChild(name)

        // No ⋮ menu on any tile. The one on your own tile offered Settings,
        // which nothing wired up (no caller passes onMore), and Leave meeting,
        // which the control row already has.
        return { id: participant.id, tile, video: pipVideoEl, name } as TileEntry
      }

      /**
       * Reconcile the grid against the current participant list.
       *
       * Add / remove / reorder by id, and swap srcObject in place when a peer's
       * stream identity changes (renegotiation hands us a new MediaStream). Only
       * tiles that actually changed are touched, so a peer joining or leaving
       * never interrupts playback on the tiles that stayed.
       */
      const renderTiles = (participants: PipParticipant[]) => {
        const next = participants.slice(0, 4)
        const nextIds = new Set(next.map((p) => p.id))

        // Drop anyone who left the call.
        for (const [id, entry] of Array.from(tileMap.entries())) {
          if (!nextIds.has(id)) {
            entry.tile.remove()
            tileMap.delete(id)
          }
        }

        for (const participant of next) {
          const existing = tileMap.get(participant.id)
          if (!existing) {
            const entry = createTile(participant)
            tileMap.set(participant.id, entry)
            grid.appendChild(entry.tile)
            continue
          }
          if (existing.name.textContent !== participant.name) {
            existing.name.textContent = participant.name
          }

          // Re-asserted every reconcile, not just at creation: a tile built
          // under earlier state (or by an earlier build of this module during a
          // hot reload) is corrected on the next sync.
          existing.tile.classList.toggle("is-local", !!participant.isLocal)

          // Video-vs-placeholder is structural: a tile built without a stream
          // has no <video> at all. Crossing that boundary (camera turned on or
          // off) means rebuilding this one tile. Checked before the re-bind
          // below so we don't bind a stream onto an element we're about to drop.
          const needsStructuralSwap =
            (!existing.video && !!participant.stream) || (!!existing.video && !participant.stream)
          if (needsStructuralSwap) {
            existing.tile.remove()
            tileMap.delete(participant.id)
            const entry = createTile(participant)
            tileMap.set(participant.id, entry)
            grid.appendChild(entry.tile)
            continue
          }

          // Re-bind only on identity change; assigning the same MediaStream
          // restarts the element and produces a visible black flash.
          if (existing.video && existing.video.srcObject !== participant.stream) {
            existing.video.srcObject = participant.stream
            existing.video.play().catch(() => { /* ignore */ })
          }
        }

        // Re-append in the caller's order so local stays first.
        for (const participant of next) {
          const entry = tileMap.get(participant.id)
          if (entry) grid.appendChild(entry.tile)
        }

        const n = tileMap.size
        grid.classList.toggle("single", n <= 1)
        grid.classList.toggle("two-tiles", n === 2)
        if (n <= 1) grid.classList.remove("vertical")
      }

      stage.appendChild(grid)
      container.appendChild(stage)

      const controlsRow = pipWindow.document.createElement("div")
      controlsRow.className = "pip-controls"

      const makeBtn = (opts: { aria: string; danger?: boolean; svg: string; onClick?: () => void }) => {
        const b = pipWindow.document.createElement("button")
        b.type = "button"
        b.className = `pip-btn${opts.danger ? " danger" : ""}`
        b.setAttribute("aria-label", opts.aria)
        b.innerHTML = opts.svg
        if (opts.onClick) b.addEventListener("click", () => opts.onClick?.())
        return b
      }

      const micBtn = makeBtn({
        aria: "Toggle microphone",
        svg: `
          <svg class="pip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3Z" />
            <path d="M19 11a7 7 0 0 1-14 0" />
            <path d="M12 18v3" />
            <path d="M8 21h8" />
            <line class="slash" x1="3" y1="3" x2="21" y2="21" />
          </svg>
        `,
        onClick: () => controlsRef.onToggleMic?.(),
      })

      const camBtn = makeBtn({
        aria: "Toggle camera",
        svg: `
          <svg class="pip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M15 10l4.553-2.276A1 1 0 0 1 21 8.618v6.764a1 1 0 0 1-1.447.894L15 14" />
            <path d="M5 18h8a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2Z" />
            <line class="slash" x1="3" y1="3" x2="21" y2="21" />
          </svg>
        `,
        onClick: () => controlsRef.onToggleCam?.(),
      })

      // The handset from the meeting's own Leave button (lucide `Phone`), so
      // leaving looks the same inside picture-in-picture as outside it.
      const leaveBtn = makeBtn({
        aria: "Leave meeting",
        danger: true,
        svg: `
          <svg class="pip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
          </svg>
        `,
        onClick: () => {
          controlsRef.onLeave?.()
          try {
            pipWindow.close()
          } catch {
            // ignore
          }
        },
      })

      controlsRow.appendChild(micBtn)
      controlsRow.appendChild(camBtn)
      controlsRow.appendChild(leaveBtn)

      container.appendChild(controlsRow)

      pipWindow.document.body.appendChild(container)

      /**
       * Mirror local mic/cam state onto the PiP buttons.
       *
       * The buttons are only ever a *view*: clicking one calls the same store
       * action the main control bar calls, and the resulting state flows back
       * here. That keeps the two surfaces from disagreeing — toggling in either
       * place updates both, because neither owns the state.
       */
      const applyMediaState = (state: PipMediaState) => {
        micBtn.classList.toggle("off", !state.micOn)
        micBtn.setAttribute("aria-pressed", String(!state.micOn))
        micBtn.setAttribute("aria-label", state.micOn ? "Mute microphone" : "Unmute microphone")
        camBtn.classList.toggle("off", !state.camOn)
        camBtn.setAttribute("aria-pressed", String(!state.camOn))
        camBtn.setAttribute("aria-label", state.camOn ? "Turn camera off" : "Turn camera on")
      }

      // Seed the window with whoever is in the call right now.
      renderTiles(participantList)
      applyMediaState(mediaState ?? { micOn: true, camOn: true })

      const layout = () => {
        try {
          const headerH = 48
          const controlsH = 64 + 14
          const padY = 14 * 2
          const padX = 12 * 2
          const availableW = Math.max(240, pipWindow.innerWidth - padX)
          const availableH = Math.max(180, pipWindow.innerHeight - headerH - controlsH - padY)
          
          const gap = 8
          let tileW: number
          let tileH: number
          
          const tiles = pipTiles()
          // Real tile count — no 2-tile floor. Alone in the call this is 1 and
          // the single tile takes the whole stage.
          const layoutCount = Math.min(4, tiles.length)

          if (layoutCount <= 1) {
            grid.classList.remove("vertical", "two-tiles")
            grid.classList.add("single")
            grid.style.gridTemplateColumns = ""
            grid.style.gridTemplateRows = ""
            grid.style.width = `${availableW}px`
            grid.style.height = `${availableH}px`
            grid.style.maxWidth = `${availableW}px`
            grid.style.maxHeight = `${availableH}px`
            tiles.forEach((t) => {
              t.tile.style.width = ""
              t.tile.style.height = ""
              t.tile.style.gridColumn = ""
              t.tile.style.gridRow = ""
            })
            return
          }

          // Always use full available space - responsive layout
          if (layoutCount <= 2) {
            // For 2 tiles, decide layout based on aspect ratio of available space
            const availableAspect = availableW / availableH
            
            if (availableAspect < 1.2) {
              // Narrow window: Stack vertically
              grid.classList.add("vertical")
              grid.classList.remove("two-tiles")
            } else {
              // Wide window: Side by side
              grid.classList.add("two-tiles")
              grid.classList.remove("vertical")
            }
            
            // Tiles automatically fill space with flex: 1
            tileW = availableW / 2
            tileH = availableH
          }
          else if (layoutCount === 3) {
            // Three participants: one large on left, two stacked on right (like Google Meet)
            const leftW = Math.floor((availableW - gap) * 0.6) // 60% for main tile
            const rightW = availableW - leftW - gap // 40% for side tiles
            const sideTileH = Math.floor((availableH - gap) / 2)
            
            // Main tile (participant 1) - left side
            tiles[0].tile.style.width = `${leftW}px`
            tiles[0].tile.style.height = `${availableH}px`
            tiles[0].tile.style.gridColumn = "1"
            tiles[0].tile.style.gridRow = "1 / 3"
            
            // Side tiles (participants 2 & 3) - right side stacked
            tiles[1].tile.style.width = `${rightW}px`
            tiles[1].tile.style.height = `${sideTileH}px`
            tiles[1].tile.style.gridColumn = "2"
            tiles[1].tile.style.gridRow = "1"
            
            tiles[2].tile.style.width = `${rightW}px`
            tiles[2].tile.style.height = `${sideTileH}px`
            tiles[2].tile.style.gridColumn = "2"
            tiles[2].tile.style.gridRow = "2"
            
            grid.style.gridTemplateColumns = `${leftW}px ${rightW}px`
            grid.style.gridTemplateRows = `${sideTileH}px ${sideTileH}px`
            grid.style.width = `${availableW}px`
            grid.style.height = `${availableH}px`
            grid.style.maxWidth = `${availableW}px`
            return // Skip the default tile sizing below
          }
          else {
            // Four participants: 2x2 grid
            const cols = 2
            const rows = 2
            const maxTileW = Math.floor((availableW - gap) / cols)
            const maxTileH = Math.floor((availableH - gap) / rows)
            
            const aspectW = 4
            const aspectH = 3
            tileW = Math.floor(Math.min(maxTileW, (maxTileH * aspectW) / aspectH))
            tileH = Math.floor((tileW * aspectH) / aspectW)
            
            grid.style.gridTemplateColumns = `repeat(${cols}, ${tileW}px)`
            grid.style.gridTemplateRows = `repeat(${rows}, ${tileH}px)`
            grid.style.width = `${tileW * cols + gap}px`
            grid.style.maxWidth = `${availableW}px`
          }
          
          // Apply sizes to tiles (for 2 and 4 participant layouts)
          if (layoutCount !== 3) {
            tiles.forEach((t) => {
              // With flexbox, tiles fill automatically via flex: 1 in CSS
              // Just clear any explicit sizing that might conflict
              t.tile.style.width = ""
              t.tile.style.height = ""
              t.tile.style.gridColumn = ""
              t.tile.style.gridRow = ""
            })
          }
          
          grid.style.maxHeight = `${availableH}px`
        } catch {
          // ignore
        }
      }

      layout()
      pipWindow.addEventListener("resize", layout)

      // Published so syncPictureInPicture can drive this window. `relayout` is
      // called after every reconcile because tile count changes the geometry.
      renderRef.current = {
        win: pipWindow,
        grid,
        tiles: tileMap,
        renderTiles: (next: PipParticipant[]) => {
          renderTiles(next)
          layout()
        },
        applyMediaState,
      }

      // Handle window close
      pipWindow.addEventListener("pagehide", () => {
        try {
          pipWindow.removeEventListener("resize", layout)
        } catch {
          // ignore
        }
        // Cleared so a later sync can't write into a torn-down document.
        renderRef.current = null
        tileMap.clear()
        setIsActive(false)
        pipWindowRef.current = null
      })

      setIsActive(true)
    } catch (error) {
      console.error("Failed to toggle Picture-in-Picture:", error)
    }
  }, [])

  /**
   * Push the current roster + media state into an open PiP window.
   *
   * Safe to call on every host render: it's a no-op when no window is open, and
   * the reconcile inside only touches tiles that actually changed.
   */
  const syncPictureInPicture = useCallback(
    (participants: PipParticipant[], mediaState?: PipMediaState) => {
      const ctx = renderRef.current
      if (!ctx) return
      // The window can be closed by the OS chrome without pagehide having run
      // yet; writing into a closed document throws.
      if (ctx.win.closed) {
        renderRef.current = null
        return
      }
      try {
        ctx.renderTiles(participants)
        if (mediaState) ctx.applyMediaState(mediaState)
      } catch {
        // Window went away mid-sync — drop the handle and let the next open rebuild.
        renderRef.current = null
      }
    },
    [],
  )

  return {
    pipState: { isActive, isSupported },
    togglePictureInPicture,
    syncPictureInPicture,
    closePictureInPicture,
  }
}
